// Shared with execution/orchestrator/htmlPlayerSizing.mjs; a parity test keeps
// the customer player and the agent's preview on the same sizing contract.
export const HTML_PLAYER_SIZE_CHANNEL = 'gab-html-player-size';
export const HTML_PLAYER_MAX_HEIGHT = 1200;

// Measure natural document height, temporarily removing viewport-derived root
// minimums. This prevents min-height:100% + padding from growing the frame on
// every ResizeObserver notification. Restore styles before the next paint.
export function measureHtmlPlayerHeight() {
  const body = document.body, root = document.documentElement;
  if (!body) return 0;
  const saved = [root.style.cssText, body.style.cssText];
  try {
    for (const el of [root, body]) {
      el.style.setProperty('height', 'auto', 'important');
      el.style.setProperty('min-height', '0', 'important');
    }
    const rect = body.getBoundingClientRect();
    const zoom = parseFloat(getComputedStyle(root).zoom) || 1;
    return Math.ceil(Math.max(body.scrollHeight * zoom, rect.height) + Math.max(0, rect.top));
  } finally {
    root.style.cssText = saved[0]; body.style.cssText = saved[1];
  }
}

export function readHtmlPlayerHeight(data, session) {
  if (!data || data.channel !== HTML_PLAYER_SIZE_CHANNEL || data.session !== session ||
      typeof data.height !== 'number' || !Number.isFinite(data.height) || data.height <= 0) return null;
  return Math.max(280, Math.min(HTML_PLAYER_MAX_HEIGHT, Math.ceil(data.height)));
}

export function injectHtmlPlayerSizing(html, session, { reportHeight = true, ensureViewport = false } = {}) {
  const token = JSON.stringify(String(session)).replace(/</g, '\\u003c');
  const script = `<script data-gab-player-sizing>(function(){
    const session=${token}, channel=${JSON.stringify(HTML_PLAYER_SIZE_CHANNEL)};
    const measure=${measureHtmlPlayerHeight.toString()};
    let timer=null, last=0, mutations, originalWidth, originalZoom;
    function fitWidth(){
      const root=document.documentElement;
      for(const [name,saved] of [['width',originalWidth],['zoom',originalZoom]]){
        if(saved[0])root.style.setProperty(name,saved[0],saved[1]);else root.style.removeProperty(name);
      }
      const available=root.clientWidth, natural=Math.max(root.scrollWidth,document.body.scrollWidth);
      if(available>0 && natural>available+1){
        root.style.setProperty('width',natural+'px','important');
        root.style.setProperty('zoom',String(available/natural),'important');
      }
    }
    const options={subtree:true,childList:true,characterData:true,attributes:true,attributeFilter:['class','style','hidden']};
    function report(){
      timer=null; mutations?.disconnect();
      let height; try{fitWidth();${reportHeight ? 'height=measure();' : ''}}finally{mutations?.observe(document.documentElement,options);}
      if(!height || height===last)return; last=height;
      const data={channel,session,height};
      if(window.ReactNativeWebView)window.ReactNativeWebView.postMessage(JSON.stringify(data));
      else parent.postMessage(data,'*');
    }
    function schedule(){if(timer===null)timer=setTimeout(report,100);}
    function start(){
      ${ensureViewport ? `// Supply a mobile viewport for fragments/desktop HTML, preserving authored viewports.
      if(!document.querySelector('meta[name="viewport" i]')){
        const viewport=document.createElement('meta');viewport.name='viewport';
        viewport.content='width=device-width, initial-scale=1';document.head.appendChild(viewport);
      }` : ''}
      const style=document.documentElement.style;
      originalWidth=[style.getPropertyValue('width'),style.getPropertyPriority('width')];
      originalZoom=[style.getPropertyValue('zoom'),style.getPropertyPriority('zoom')];
      mutations=new MutationObserver(schedule);mutations.observe(document.documentElement,options);
      if(window.ResizeObserver){const observer=new ResizeObserver(schedule);observer.observe(document.body);}
      window.addEventListener('resize',schedule);document.addEventListener('load',schedule,true);
      document.fonts?.ready.then(schedule);schedule();
    }
    if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',start,{once:true});else start();
  })();<\/script>`;
  return /<\/body\s*>/i.test(html) ? html.replace(/<\/body\s*>/i, script + '\n</body>') : html + script;
}
